import { useMemo, useState } from 'react';
import { FilterMatchMode } from 'primereact/api';
import {
    useGetLmsTenderRequestsQuery,
    useAddLmsTenderRequestMutation,
    useUpdateLmsTenderRequestMutation,
    useDeleteLmsTenderRequestMutation,
    useChangeLmsCreateRequestStatusMutation
} from '../../../../api';
import toastService from '../../../../utils/toastService';
import { formatErrorMessage } from '../../../../utils/errorUtils';
import { useLanguage } from '../../../../contexts/LanguageContext';
import { matchModeToOperator } from '../../../../utils/filterUtils';
import { TENDER_REQUEST_TYPE } from '../utils/tenderRequestStatus';

// Kolon adları PascalCase dönüşüm konvansiyonuna göre (bkz. training-request/hooks/
// useTrainingRequestData.js'teki aynı desen) — doküman yalnızca vehicleModel/tenderRequestType/
// status için tam bir GetList örneği veriyor (docs/LMS-IHALE--FE-Dokumani.txt §1.4), diğer
// alanlar (numberOfTechnician/numberOfDrivers/trainingLanguage/createdName) bu konvansiyona
// göre türetildi ve BE (GetTenderRequestListQueryHandler.cs'teki BuildProjection) ile doğrulandı.
// NOT: `location` (lokasyonlar) filtrelenebilir projeksiyonun parçası DEĞİL — RequestLocationT
// ayrı bir tablo, sayfalama SONRASI ayrıca sorgulanıyor — bu yüzden kolon filtresi eklenmedi
// (eklenseydi ApplyColumnFilter sessizce no-op olurdu, bkz. DynamicFilterExtensions.cs).
const BACKEND_COLUMN_MAP = {
    vehicleModel: 'VehicleModel',
    tenderRequestType: 'TenderRequestType',
    numberOfTechnician: 'NumberOfTechnician',
    numberOfDrivers: 'NumberOfDrivers',
    preferTrainingDate: 'PreferTrainingDate',
    alternativeDate: 'AlternativeDate',
    trainingLanguage: 'TrainingLanguage',
    status: 'Status',
    createdDate: 'CreatedDate',
    createdName: 'CreatedName'
};

const EXACT_FILTER_FIELDS = ['vehicleModel', 'tenderRequestType', 'status'];
const TEXT_FILTER_FIELDS = ['numberOfTechnician', 'numberOfDrivers', 'trainingLanguage', 'createdName'];
const DATE_FILTER_FIELDS = ['preferTrainingDate', 'alternativeDate', 'createdDate'];

const INITIAL_FILTER_STATE = {
    vehicleModel: null,
    tenderRequestType: null,
    status: null,
    numberOfTechnician: null,
    numberOfTechnicianMatchMode: null,
    numberOfDrivers: null,
    numberOfDriversMatchMode: null,
    trainingLanguage: null,
    trainingLanguageMatchMode: null,
    createdName: null,
    createdNameMatchMode: null,
    preferTrainingDate: null,
    alternativeDate: null,
    createdDate: null
};

const buildDynamicFilters = (filtersState) => {
    const filters = [];
    const pushFilter = (field, value, matchMode) => {
        if (value === null || value === undefined || value === '') return;
        const column = BACKEND_COLUMN_MAP[field];
        if (!column) return;

        let filterValue;
        if (DATE_FILTER_FIELDS.includes(field) && value instanceof Date) {
            filterValue = value.toISOString();
        } else {
            filterValue = String(value).trim();
            if (!filterValue) return;
        }

        filters.push({ column, operator: matchModeToOperator[matchMode] ?? matchModeToOperator[FilterMatchMode.EQUALS], value: filterValue });
    };

    EXACT_FILTER_FIELDS.forEach((field) => pushFilter(field, filtersState?.[field], FilterMatchMode.EQUALS));
    TEXT_FILTER_FIELDS.forEach((field) => pushFilter(field, filtersState?.[field], filtersState?.[`${field}MatchMode`] ?? FilterMatchMode.CONTAINS));
    DATE_FILTER_FIELDS.forEach((field) => pushFilter(field, filtersState?.[field], FilterMatchMode.DATE_IS));

    return filters;
};

const buildDynamicSorts = (field, order) => {
    if (!field || !order) return [{ column: 'CreatedDate', direction: 2 }];
    const column = BACKEND_COLUMN_MAP[field] || field;
    return [{ column, direction: order === 1 ? 1 : 2 }];
};

/**
 * İhale Talebi (LmsTenderRequest) ekranı için CRUD + statü değişikliği veri/state hook'u. Bkz.
 * docs/LMS-IHALE--FE-Dokumani.txt. Kullanıcı isteği: sekmeli yapıya gerek yok, tek ekranda
 * listeleme + ekle/düzenle diyaloğu + onay akışı (Tüm Talepler'deki ile aynı desen, ama tek
 * tip — sekme/tür seçimi yok).
 */
const useTenderRequestData = () => {
    const { th } = useLanguage();
    const [lazyParams, setLazyParams] = useState({ first: 0, rows: 10, page: 1 });
    const [activeFilters, setActiveFilters] = useState(INITIAL_FILTER_STATE);
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState(null);

    const queryParams = useMemo(
        () => ({
            pageNumber: lazyParams.page,
            pageSize: lazyParams.rows,
            filters: buildDynamicFilters(activeFilters),
            sorts: buildDynamicSorts(sortField, sortOrder)
        }),
        [lazyParams, activeFilters, sortField, sortOrder]
    );

    const { data: tenderRequestApi, isLoading, isFetching, error, refetch } = useGetLmsTenderRequestsQuery(queryParams, { refetchOnMountOrArgChange: true });

    const [addMutation] = useAddLmsTenderRequestMutation();
    const [updateMutation] = useUpdateLmsTenderRequestMutation();
    const [deleteMutation] = useDeleteLmsTenderRequestMutation();
    const [changeStatusMutation] = useChangeLmsCreateRequestStatusMutation();

    const saveRequest = async (formData) => {
        try {
            if (formData.id) {
                await updateMutation(formData).unwrap();
            } else {
                await addMutation(formData).unwrap();
            }
            toastService.success(th('tenderRequest.saveSuccess'), th('common.success'));
            refetch();
            return true;
        } catch (err) {
            toastService.error(formatErrorMessage(err) || th('tenderRequest.saveError'), th('common.error'));
            return false;
        }
    };

    const deleteRequest = async (id) => {
        await deleteMutation(id).unwrap();
        refetch?.();
    };

    // Onay akışı ortak uçtan (LmsCreateRequest/ChangeStatus) requestType: 3 ile — bkz.
    // docs/LMS-IHALE--FE-Dokumani.txt §4.
    const changeStatus = async ({ id, newStatus, comment }) => {
        await changeStatusMutation({ id, newStatus, comment, requestType: TENDER_REQUEST_TYPE }).unwrap();
        refetch?.();
    };

    const onPage = (event) => {
        setLazyParams((prev) => ({
            ...prev,
            page: event.page + 1,
            first: event.first,
            rows: event.rows
        }));
    };

    const onFiltersChange = (updated) => {
        setActiveFilters(updated);
        setLazyParams((prev) => ({ ...prev, first: 0, page: 1 }));
    };

    const initFilters = () => {
        setActiveFilters(INITIAL_FILTER_STATE);
        setLazyParams((prev) => ({ ...prev, first: 0, page: 1 }));
    };

    const onSortChange = (field, order) => {
        setSortField(field);
        setSortOrder(order);
    };

    return {
        tenderRequestApi: tenderRequestApi || { items: [], totalCount: 0 },
        isLoading,
        isFetching,
        error,
        lazyParams,
        onPage,
        activeFilters,
        onFiltersChange,
        initFilters,
        sortField,
        sortOrder,
        onSortChange,
        saveRequest,
        deleteRequest,
        changeStatus,
        refetch
    };
};

export default useTenderRequestData;
